import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

/**
 * Virtual scrolling list component for rendering large datasets efficiently.
 *
 * Only renders items that are currently visible in the viewport, plus a configurable
 * overscan buffer to prevent flickering during scroll.
 *
 * @example
 * ```typescript
 * <VirtualList
 *   items={events}
 *   itemHeight={80}
 *   renderItem={(event, index) => <EventCard key={event.id} event={event} />}
 *   overscan={3}
 *   className="h-full"
 * />
 * ```
 */

export interface VirtualListProps<T> {
  /**
   * Array of items to render
   */
  items: T[];

  /**
   * Height of each item in pixels. Can be:
   * - number: Fixed height for all items
   * - function: Dynamic height based on item
   */
  itemHeight: number | ((item: T, index: number) => number);

  /**
   * Render function for each item
   */
  renderItem: (item: T, index: number) => React.ReactNode;

  /**
   * Number of items to render outside visible area (prevents flickering during scroll)
   * Default: 3
   */
  overscan?: number;

  /**
   * Container className
   */
  className?: string;

  /**
   * Container style
   */
  style?: React.CSSProperties;

  /**
   * Custom key extractor (default: uses index)
   */
  getItemKey?: (item: T, index: number) => string | number;

  /**
   * Callback when user scrolls to bottom (for infinite loading)
   */
  onEndReached?: () => void;

  /**
   * Threshold (in pixels) from bottom to trigger onEndReached
   * Default: 300
   */
  onEndReachedThreshold?: number;

  /**
   * Loading indicator to show at bottom when loading more
   */
  loadingIndicator?: React.ReactNode;

  /**
   * Whether currently loading more items
   */
  isLoading?: boolean;

  /**
   * Empty state component when items array is empty
   */
  emptyState?: React.ReactNode;
}

/**
 * Virtual scrolling list component.
 *
 * Renders only visible items for optimal performance with large datasets.
 * Supports:
 * - Fixed or dynamic item heights
 * - Overscan buffering
 * - Infinite scrolling
 * - Custom empty states
 * - Smooth scrolling
 *
 * Performance characteristics:
 * - O(1) render time regardless of list size
 * - Minimal re-renders using React.memo and callbacks
 * - Efficient scroll handling with requestAnimationFrame
 */
export function VirtualList<T>({
  items,
  itemHeight,
  renderItem,
  overscan = 3,
  className = '',
  style,
  getItemKey,
  onEndReached,
  onEndReachedThreshold = 300,
  loadingIndicator,
  isLoading = false,
  emptyState,
}: VirtualListProps<T>) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [containerHeight, setContainerHeight] = useState(0);
  const scrollRafRef = useRef<number | undefined>(undefined);
  const endReachedCalledRef = useRef(false);

  // Calculate item height for each item
  const getHeight = useCallback(
    (item: T, index: number): number => {
      return typeof itemHeight === 'function'
        ? itemHeight(item, index)
        : itemHeight;
    },
    [itemHeight]
  );

  // Memoize cumulative heights for O(log n) range calculations
  const heights = useMemo(() => {
    const result: number[] = [];
    let cumulative = 0;

    for (let i = 0; i < items.length; i++) {
      result.push(cumulative);
      cumulative += getHeight(items[i], i);
    }

    result.push(cumulative); // Total height
    return result;
  }, [items, getHeight]);

  const totalHeight = heights[heights.length - 1] || 0;

  // Binary search to find first visible item
  const findStartIndex = useCallback(
    (scrollTop: number): number => {
      let low = 0;
      let high = heights.length - 2;

      while (low <= high) {
        const mid = Math.floor((low + high) / 2);
        const midHeight = heights[mid];

        if (midHeight < scrollTop) {
          low = mid + 1;
        } else {
          high = mid - 1;
        }
      }

      return Math.max(0, low - 1);
    },
    [heights]
  );

  // Find last visible item
  const findEndIndex = useCallback(
    (scrollTop: number, containerHeight: number): number => {
      const scrollBottom = scrollTop + containerHeight;
      let low = 0;
      let high = heights.length - 2;

      while (low <= high) {
        const mid = Math.floor((low + high) / 2);
        const midHeight = heights[mid];

        if (midHeight <= scrollBottom) {
          low = mid + 1;
        } else {
          high = mid - 1;
        }
      }

      return Math.min(items.length - 1, low);
    },
    [heights, items.length]
  );

  // Calculate visible range with overscan
  const visibleRange = useMemo(() => {
    const start = findStartIndex(scrollTop);
    const end = findEndIndex(scrollTop, containerHeight);

    return {
      start: Math.max(0, start - overscan),
      end: Math.min(items.length - 1, end + overscan),
    };
  }, [
    scrollTop,
    containerHeight,
    findStartIndex,
    findEndIndex,
    overscan,
    items.length,
  ]);

  // Handle scroll with RAF for better performance
  const handleScroll = useCallback(() => {
    if (scrollRafRef.current) {
      cancelAnimationFrame(scrollRafRef.current);
    }

    scrollRafRef.current = requestAnimationFrame(() => {
      if (containerRef.current) {
        const newScrollTop = containerRef.current.scrollTop;
        setScrollTop(newScrollTop);

        // Check if we should trigger onEndReached
        if (onEndReached && !isLoading && !endReachedCalledRef.current) {
          const scrollBottom = newScrollTop + containerHeight;
          const distanceFromBottom = totalHeight - scrollBottom;

          if (distanceFromBottom <= onEndReachedThreshold) {
            endReachedCalledRef.current = true;
            onEndReached();
          }
        }
      }
    });
  }, [
    containerHeight,
    totalHeight,
    onEndReached,
    onEndReachedThreshold,
    isLoading,
  ]);

  // Reset end reached flag when loading completes
  useEffect(() => {
    if (isLoading === false) {
      endReachedCalledRef.current = false;
    }
  }, [isLoading]);

  // Measure container height
  useEffect(() => {
    if (!containerRef.current) {
      return;
    }

    const updateHeight = () => {
      if (containerRef.current) {
        setContainerHeight(containerRef.current.clientHeight);
      }
    };

    updateHeight();

    const resizeObserver = new ResizeObserver(updateHeight);
    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
      if (scrollRafRef.current) {
        cancelAnimationFrame(scrollRafRef.current);
      }
    };
  }, []);

  // Render empty state
  if (items.length === 0 && emptyState) {
    return (
      <div className={className} style={style}>
        {emptyState}
      </div>
    );
  }

  // Render visible items
  const visibleItems = [];
  for (let i = visibleRange.start; i <= visibleRange.end; i++) {
    const item = items[i];
    const key = getItemKey ? getItemKey(item, i) : i;
    const top = heights[i];

    visibleItems.push(
      <div
        key={key}
        style={{
          position: 'absolute',
          top: `${top}px`,
          left: 0,
          right: 0,
          height: `${getHeight(item, i)}px`,
        }}
      >
        {renderItem(item, i)}
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className={className}
      style={{
        ...style,
        overflow: 'auto',
        position: 'relative',
      }}
      onScroll={handleScroll}
    >
      {/* Spacer to maintain total height */}
      <div
        style={{
          height: `${totalHeight}px`,
          position: 'relative',
        }}
      >
        {visibleItems}
      </div>

      {/* Loading indicator */}
      {isLoading && loadingIndicator && (
        <div
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
          }}
        >
          {loadingIndicator}
        </div>
      )}
    </div>
  );
}

/**
 * Memoized version of VirtualList for better performance.
 * Use this when parent component re-renders frequently.
 */
export const VirtualListMemo = React.memo(VirtualList) as typeof VirtualList;
